<script setup lang="ts">
/**
 * Full-screen viewer for the conversation's photos and videos, like WhatsApp Web: the current
 * item large, caption below, arrows and ←/→ to move, thumbnails at the bottom, download and close.
 */
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import Icon from "./Icon.vue";
import IconButton from "./IconButton.vue";
import type { Message } from "../data/demo";
import { useEscapeLayer } from "../composables/escape";
import { dateLabel } from "../composables/chatTimeline";
import { t } from "../i18n";

const props = defineProps<{
  items: Message[];
  startId: string;
  /** Name shown for received items. */
  contactName?: string;
}>();
const emit = defineEmits<{ close: [] }>();

const index = ref(
  Math.max(
    0,
    props.items.findIndex((m) => m.id === props.startId),
  ),
);
const strip = ref<HTMLElement>();
const current = computed(() => props.items[index.value] ?? null);
const author = computed(() =>
  current.value?.out ? t("media.viewer.you") : (props.contactName ?? ""),
);

function go(step: number) {
  const next = index.value + step;
  if (next >= 0 && next < props.items.length) index.value = next;
}
function key(e: KeyboardEvent) {
  if (e.key === "ArrowRight") go(1);
  else if (e.key === "ArrowLeft") go(-1);
  else return;
  e.preventDefault();
}
function download() {
  const m = current.value;
  if (!m?.src) return;
  const a = document.createElement("a");
  a.href = m.downloadUrl || m.src;
  a.download = m.name || (m.kind === "video" ? "video.mp4" : "foto.jpg");
  a.click();
}
watch(index, async () => {
  await nextTick();
  strip.value
    ?.querySelector<HTMLElement>(".lightbox-thumb.active")
    ?.scrollIntoView({
      block: "nearest",
      inline: "center",
      behavior: "smooth",
    });
});
useEscapeLayer(
  () => true,
  () => emit("close"),
);
onMounted(() => document.addEventListener("keydown", key));
onBeforeUnmount(() => document.removeEventListener("keydown", key));
</script>

<template>
  <Teleport to="body">
    <div
      class="media-lightbox"
      role="dialog"
      aria-modal="true"
      :aria-label="t('media.viewer.label')"
    >
      <header class="lightbox-head">
        <span class="lightbox-who grow">
          <strong>{{ author }}</strong>
          <small v-if="current"
            >{{ current.date ? dateLabel(current.date) + " · " : ""
            }}{{ current.time }}</small
          >
        </span>
        <IconButton
          icon="download"
          :label="t('message.download')"
          @click="download"
        />
        <IconButton
          icon="close"
          :label="t('common.close')"
          @click="emit('close')"
        />
      </header>
      <div class="lightbox-stage">
        <button
          type="button"
          class="lightbox-nav prev"
          :disabled="index === 0"
          :aria-label="t('media.viewer.previous')"
          @click="go(-1)"
        >
          <Icon name="back" :size="24" />
        </button>
        <figure v-if="current" class="lightbox-figure">
          <img
            v-if="current.kind !== 'video'"
            :key="current.id"
            :src="current.src"
            :alt="current.text"
          />
          <video
            v-else
            :key="current.id"
            :src="current.src"
            controls
            autoplay
            playsinline
          />
          <figcaption v-if="current.text">{{ current.text }}</figcaption>
        </figure>
        <button
          type="button"
          class="lightbox-nav next"
          :disabled="index >= items.length - 1"
          :aria-label="t('media.viewer.next')"
          @click="go(1)"
        >
          <Icon name="back" :size="24" />
        </button>
      </div>
      <div v-if="items.length > 1" ref="strip" class="lightbox-strip">
        <button
          v-for="(m, i) in items"
          :key="m.id"
          type="button"
          class="lightbox-thumb"
          :class="{ active: i === index }"
          :aria-label="
            t('media.viewer.item', { n: i + 1, total: items.length })
          "
          @click="index = i"
        >
          <img v-if="m.kind !== 'video'" :src="m.src" alt="" loading="lazy" />
          <video
            v-else
            :src="m.src ? m.src + '#t=0.1' : undefined"
            preload="metadata"
            muted
          />
        </button>
      </div>
    </div>
  </Teleport>
</template>
